/* eslint-disable complexity */

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ClareSystem, ClareSystemData, ClareSystemHelper } from "@app/project/model/clare-system";
import { checksumValid, iccidValid, numberOnlyValid } from "@app/shared/validators";
import jsQR, { QRCode } from "jsqr";
import { BehaviorSubject, Observable, Subscription, combineLatest, forkJoin, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, finalize, map, take, tap } from "rxjs/operators";

import { ActivatedRoute } from "@angular/router";
import { AccountStatusMessages } from "@app/project/components/list-projects/list-projects.component";
import { SecuritySystem } from "@app/project/model/security-system";
import { CHServiceService } from "@app/project/services/ch-services.service";
import { ClareSystemDbService } from "@app/project/services/clare-system-db.service";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { ProjectService } from "@app/project/services/project.service";
import { ClareSystemActions } from "@app/project/store/actions";
import { RalinkDatabaseService } from "@app/shared/databases/ralink.database";
import { NgChanges } from "@app/shared/model/ng-changes.model";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { ErrorToSuppress } from "@app/shared/services/db-crud-helper";
import { Action } from "@ngrx/store";
import { ClareControllerInputConfirmationComponent } from "../clare-controller-input-confirmation/clare-controller-input-confirmation.component";

@Component({
    selector: "clare-controller-input",
    templateUrl: "./clare-controller-input.component.html",
    styleUrls: ["./clare-controller-input.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClareControllerInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input() claresystem: ClareSystem = ClareSystemHelper.getDefault();
    @Input() securitysystem: SecuritySystem = new SecuritySystem(null);
    @Input() customerEmail = "";
    @Input() noscroll: boolean;
    @Input() isAddPanel = false;
    @Input() isInsideWizard = false;
    @Input() isReadOnly = false;
    currentUUID: string;
    @Output()
    onControllerUpdate: EventEmitter<Action[]> = new EventEmitter();
    @ViewChild("controllerIdEl", { static: true }) canvasElement;
    canvasContext: CanvasRenderingContext2D;
    video: HTMLVideoElement;
    qrcodeDetected: boolean;
    videoDisabled = true;
    validateForMini = true;
    controllerLabel = "UUID";
    serialNumberLabel = "Serial Number";
    imageData: BehaviorSubject<ImageData> = new BehaviorSubject(null);
    isValidUuid = false;
    isControllerVerified = false;
    isValidSerialNumber = true;
    isPendingVerification = false;
    isSerialNumberVerified: Observable<boolean> = of(false);
    raLinkConnectionValid = null;
    raLinkConnectionFailureMessage = "";
    subscriptions: Subscription[] = [];
    projectExists = false;
    requiresVerification = false;
    isControllerOnline = true;
    cliqAddressInput: FormGroup = this.formBuilder.group({
        controllerId: [""],
        serialNumber: [""],
        meid: [""],
        imei: ["", [numberOnlyValid, checksumValid]],
        iccid: ["", [iccidValid, checksumValid]],
        macAddress: [""],
        securityMacAddress: [""],
    });
    validateButtonMashed = false;
    verificationFieldName: string = null;
    isIncorrectSerialNumber = false;
    isClareOne = true;
    servicesChecker: {
        type?: string;
        message?: string;
    } = {};
    checkingService = {
        loading: false,
        cellularAvailable: false,
        alulaAvailable: false,
    };

    isExistingProjectThisProject = false;

    private clareSystemHelper = new ClareSystemHelper();

    constructor(
        private clareSystemDbService: ClareSystemDbService,
        private formBuilder: FormBuilder,
        private ralinkDatabaseService: RalinkDatabaseService,
        private chService: CHServiceService,
        private projectService: ProjectService,
        private activatedRoute: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog
    ) {}
    ngOnChanges(changes: NgChanges<ClareControllerInputComponent>): void {
        if (changes.isReadOnly?.currentValue) {
            this.cliqAddressInput.disable();
        }
    }

    get fields() {
        return this.cliqAddressInput.controls;
    }

    get notAbleToSaveController() {
        return (
            this.isReadOnly ||
            !this.serviceAvailableToUpdate ||
            (!this.isControllerVerified && this.requiresVerification) ||
            this.raLinkConnectionValid === null ||
            !this.isValidUuid ||
            (this.isClareOne && !this.isValidSerialNumber) ||
            !this.cliqAddressInput.valid
        );
    }

    private get serviceAvailableToUpdate() {
        return this.checkingService.alulaAvailable || this.checkingService.cellularAvailable;
    }

    get saveButtonLabel(): string {
        if (this.isAddPanel) {
            return "Add Controller";
        }

        return "Save Controller";
    }

    getBaseValue(value) {
        return (value || "").replace(/:/g, "").replace(/-/g, "").replace(/\./g, "");
    }

    ngOnInit() {
        this.initAddressInput();

        this.setUpWatchingControllerIdChange();

        this.setUpWatchingSerialNumberChange();

        this.subscriptions.push(this.imageData.subscribe(imageData => this.highlightQrCodeAndparseData(imageData)));
        if (this.canvasElement) {
            this.canvasContext = (this.canvasElement.nativeElement as HTMLCanvasElement).getContext("2d");
            this.video = document.createElement("video");
        }

        this.checkIfServicesDisabled();
    }

    private initAddressInput() {
        if (this.claresystem) {
            this.cliqAddressInput.setValue({
                controllerId: this.claresystem.uuid || "",
                serialNumber: this.claresystem.serialNumber || "",
                meid: this.securitysystem?.meid || "",
                imei: this.securitysystem?.imei || "",
                iccid: this.securitysystem?.iccid || "",
                macAddress: this.claresystem.macAddress || "",
                securityMacAddress: this.securitysystem?.macAddress || "",
            });

            this.validateUuid(this.claresystem.uuid);
            this.currentUUID = this.claresystem.uuid;
            if (!this.currentUUID) {
                this.checkAndUpdateFromUrlParams();
            }
        }
    }

    private checkAndUpdateFromUrlParams() {
        const { uuid, iccid, serial } = this.activatedRoute.snapshot.queryParams;
        const isValidController = this.validateUuid(uuid);
        if (isValidController) {
            this.cliqAddressInput.patchValue({
                iccid,
                controllerId: uuid || "",
                serialNumber: serial || "",
            });
        }
    }

    private setUpWatchingControllerIdChange() {
        this.subscriptions.push(
            this.cliqAddressInput.controls.controllerId.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(value => {
                this.validateUuid(value);
                this.raLinkConnectionValid = null;
                this.projectExists = false;
                this.requiresVerification = false;
                this.isExistingProjectThisProject = false;
                this.isControllerVerified = false;

                if (this.isValidUuid) {
                    this.getVerificationRequirements();
                }
            })
        );
    }

    private setUpWatchingSerialNumberChange() {
        this.subscriptions.push(
            this.cliqAddressInput.controls.serialNumber.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(value => {
                this.validateSerialNumber(value);
            })
        );
    }

    private checkIfServicesDisabled() {
        this.checkingService.loading = true;
        this.cliqAddressInput.disable();
        forkJoin([
            this.projectService.isCellularActive(this.securitysystem),
            this.projectService.isAlulaActive(this.securitysystem?.macAddress),
        ])
            .pipe(
                take(1),
                tap(([cellularActive, alulaActive]) => {
                    this.confirmDisabledServices(cellularActive, alulaActive);
                }),
                catchError(e => {
                    this.servicesChecker = {
                        type: "error",
                        message: `The status of the activated services cannot be confirmed.
                             The controller information cannot be updated at this time. Please try again.`,
                    };
                    return of(e);
                }),
                finalize(() => {
                    this.checkingService.loading = false;
                })
            )
            .subscribe(([cellularActive, alulaActive]) => {
                this.checkingService.cellularAvailable = !cellularActive;
                this.checkingService.alulaAvailable = !alulaActive;
                this.enableFormFields(cellularActive, alulaActive);
            });
    }

    private enableFormFields(cellularActive: any, alulaActive: any) {
        if (!(cellularActive || alulaActive)) {
            this.cliqAddressInput.enable();
        } else {
            if (!cellularActive) {
                this.fields.iccid?.enable();
            }
            if (!alulaActive) {
                this.fields.controllerId?.enable();
                this.fields.macAddress?.enable();
                this.fields.securityMacAddress?.enable();
            }
        }
    }

    private confirmDisabledServices(cellularActive: boolean, alulaActive: boolean) {
        if (cellularActive || alulaActive) {
            const activatedServices = [];

            if (cellularActive) {
                activatedServices.push("cellular");
            }

            if (cellularActive && alulaActive) {
                activatedServices.push("and");
            }

            if (alulaActive) {
                activatedServices.push("security monitoring");
            }

            const message = AccountStatusMessages.deactivateSpWarning.replace("_s_", activatedServices.join(" "));
            this.servicesChecker = {
                type: "warning",
                message,
            };
        } else {
            this.servicesChecker = null;
        }
    }

    checkAndGetController(clareSystemData: ClareSystemData) {
        if (clareSystemData) {
            this.claresystem = this.clareSystemHelper.fromDbObject(clareSystemData);
        } else {
            this.claresystem = ClareSystemHelper.getDefault();
        }
    }

    getVerificationRequirements() {
        const formUuid = this.cliqAddressInput.controls.controllerId.value;
        const projectExists$ = this.clareSystemDbService.getProjectIdByUuid(formUuid).pipe(map(id => id !== null));
        const clareSystem$ = this.clareSystemDbService.getClareSystemByUuid(formUuid, [ErrorToSuppress.PERMISSION_DENIED_ERROR]).pipe(
            catchError((error: DataServiceError<{ error: string }>) => {
                // permission denied error thrown when user exists, but in a different company
                if (error.error === ErrorToSuppress.PERMISSION_DENIED_ERROR) {
                    this.projectExists = true;
                    this.isExistingProjectThisProject = false;
                }

                throw new DataServiceError(error.error);
            })
        );

        return combineLatest([clareSystem$, projectExists$])
            .pipe(
                tap(([clareSystemData, projectExists]) => {
                    this.handleExistedSystem(clareSystemData, projectExists);
                })
            )
            .subscribe();
    }

    private handleExistedSystem(clareSystemData: ClareSystemData, projectExists = false) {
        if (clareSystemData) {
            if (projectExists) {
                // Project exists
                this.projectExists = true;
                this.requiresVerification = false;
                if (clareSystemData.uuid === this.currentUUID) {
                    // existing project is this project
                    this.isExistingProjectThisProject = true;
                }
            } else {
                // Project Does Not Exist
                this.projectExists = false;
                this.requiresVerification = true;
            }
        } else {
            this.projectExists = false;
            this.requiresVerification = false;
        }

        if (!this.projectExists) {
            this.checkAndGetController(clareSystemData);
        }
    }

    submitForm() {
        const newUuid = this.cliqAddressInput.controls.controllerId.value;
        if (this.currentUUID && newUuid !== this.currentUUID) {
            this.openConfirmationDialog();
        } else {
            this.saveController();
        }
    }

    saveController() {
        let securitysystem;
        const {
            serialNumber = "",
            controllerId = "",
            macAddress = "",
            securityMacAddress = "",
            iccid,
        } = this.cliqAddressInput.getRawValue() || {};
        if (!this.claresystem.serialNumber) {
            const validatedSerialNumber = this.validateSerialNumber(serialNumber);
            this.claresystem.serialNumber = validatedSerialNumber;
        }
        this.claresystem.macAddress = macAddress;
        this.claresystem.serialNumber = serialNumber;

        this.claresystem.uuid = controllerId;

        const events: Action[] = [ClareSystemActions.update({ claresystem: this.claresystem })];
        if (iccid || securityMacAddress || this.isClareOne) {
            let mac = null;
            if (this.isClareOne) {
                mac = macAddress;
            } else {
                mac = securityMacAddress;
            }
            securitysystem = Object.assign(this.securitysystem, {
                macAddress: mac,
                name: "Security",
                iccid,
            });
            events.push({ type: "FakeUpdateSecurityAction", payload: securitysystem } as Action);
        }
        // These actions do not get dispatched.
        return this.onControllerUpdate.emit(events);
    }

    openConfirmationDialog() {
        const dialogRef: MatDialogRef<ClareControllerInputConfirmationComponent> = this.dialog.open(
            ClareControllerInputConfirmationComponent,
            { width: "350px" }
        );

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.saveController();
            }
        });
    }

    ngOnDestroy() {
        this.stopCamera();

        if (this.subscriptions.length) {
            this.subscriptions.forEach(subscription => subscription.unsubscribe());
        }
    }

    stopCamera() {
        if (this.video && this.video.srcObject instanceof MediaStream) {
            this.video.srcObject.getTracks().forEach(track => track.stop());
            this.videoDisabled = true;
        }
    }

    highlightQrCodeAndparseData(imageData) {
        if (imageData) {
            const code: QRCode = jsQR(imageData.data, imageData.width, imageData.height);
            if (code) {
                this.drawLine(code.location.topLeftCorner, code.location.topRightCorner, "#FF3B58");
                this.drawLine(code.location.topRightCorner, code.location.bottomRightCorner, "#FF3B58");
                this.drawLine(code.location.bottomRightCorner, code.location.bottomLeftCorner, "#FF3B58");
                this.drawLine(code.location.bottomLeftCorner, code.location.topLeftCorner, "#FF3B58");

                if (code.data) {
                    this.qrcodeDetected = true;
                }

                try {
                    const parsedCode = JSON.parse(code.data.replace(/[\n\r]/g, ""));
                    const validController = {
                        uuid: this.validateUuid(parsedCode["uuid"] || parsedCode["UUID"], true),
                        serialNumber: this.validateSerialNumber(parsedCode["serial"] || parsedCode["Serial"]),
                        imei: parsedCode["imei"] || parsedCode["IMEI"] || "",
                        iccid: parsedCode["iccid"] || parsedCode["ICCID"] || "",
                        macAddress: parsedCode["mac"] || parsedCode["MAC"] || "",
                    };

                    if (validController) {
                        this.cliqAddressInput.patchValue({
                            controllerId: validController.uuid,
                            serialNumber: validController.serialNumber,
                            imei: validController.imei,
                            iccid: validController.iccid,
                            macAddress: validController.macAddress,
                        });

                        if (this.video.srcObject instanceof MediaStream) {
                            this.video.srcObject.getTracks().forEach(track => track.stop());
                            this.videoDisabled = true;
                        }
                        this.isValidUuid = true;
                    }
                } catch (e) {
                    if (typeof code.data !== "object") {
                        const validUuid = this.validateUuid(code.data, true);
                        if (validUuid) {
                            this.cliqAddressInput.patchValue({
                                controllerId: validUuid,
                            });

                            if (this.video.srcObject instanceof MediaStream) {
                                this.video.srcObject.getTracks().forEach(track => track.stop());
                                this.videoDisabled = true;
                            }
                        }
                    }
                }
            }
        }
    }

    switchValidateForMini(value) {
        this.validateForMini = value;
        this.validateUuid(this.cliqAddressInput.controls.controllerId.value);

        if (!value) {
            this.controllerLabel = "CLIQ.Host UUID";
        } else {
            this.controllerLabel = "UUID";
        }
    }

    isValidMiniUuidLength(uuid) {
        return uuid.length === 12;
    }

    isValidHostUuidLength(uuid) {
        return uuid.length === 28;
    }

    extractExtension(uuid) {
        return [uuid.slice(0, uuid.length - 3), "." + uuid.slice(uuid.length - 3, uuid.length)];
    }

    validateUuid(value, isAScan = false) {
        let isValid = false;
        const originalUUID = this.getBaseValue(value);

        if (originalUUID) {
            let uuid = "";
            let extension = "";
            const uuidLength = originalUUID.length;

            // it is a Host UUID
            if (uuidLength === 31 || uuidLength === 28) {
                this.validateForMini = false;
            } else {
                this.validateForMini = true;
            }
            // if it has an extension, extract it
            if ((this.validateForMini && uuidLength === 15) || uuidLength === 31) {
                [uuid, extension] = this.extractExtension(originalUUID);
                isValid = true;
                this.isValidUuid = true;
            } else {
                uuid = originalUUID;
                if (this.isValidMiniUuidLength(uuid) || this.isValidHostUuidLength(uuid)) {
                    isValid = true;
                    this.isValidUuid = true;
                }
            }

            if ((isValid && isAScan) || !isAScan) {
                if (this.validateForMini) {
                    const matches = uuid.match(/([0-9a-fA-F]){1,2}/g) || [originalUUID];
                    if (this.isValidMiniUuidLength(uuid) && matches.length === 6) {
                        isValid = true;
                        this.isValidUuid = true;
                        const macAddress = this.getMACFromHost(uuid);
                        this.setClareOneDevice(macAddress);
                        this.cliqAddressInput.controls.macAddress.setValue(this.getMACFromHost(uuid));
                    } else {
                        isValid = false;
                        this.isValidUuid = false;
                    }
                    // Pattern for CLIQ.Mini and All-in-One
                    uuid = matches.join(":").toUpperCase() + extension;
                } else {
                    const matches = uuid.match(/([0-9a-fA-F]){1,4}/g);
                    if (uuid.length === 28 && matches.length === 7) {
                        // Pattern for CLIQ.Host and CHOS-L
                        isValid = true;
                        this.isValidUuid = true;
                        this.cliqAddressInput.controls.macAddress.setValue(this.getMACFromHost(uuid));
                    } else {
                        isValid = false;
                        this.isValidUuid = false;
                    }

                    uuid = matches.slice(0, 5).join("-").toUpperCase() + matches.slice(5, 7).join("").toUpperCase() + extension;
                }
            } else {
                uuid = "";
            }

            if (!isValid) {
                this.cliqAddressInput.markAsTouched();
                this.cliqAddressInput.markAsDirty();
                this.fields.controllerId.setErrors({
                    invalid: "Invalid UUID",
                });
            } else {
                this.cliqAddressInput.setErrors(null);
            }
            if (this.fields.controllerId.value !== uuid) {
                this.cliqAddressInput.patchValue({ controllerId: uuid });
            }

            return uuid.toUpperCase();
        }

        this.isValidUuid = false;
    }

    setClareOneDevice(uuid: string) {
        this.isClareOne = ProjectHelperService.isClareOne(uuid);
        if (this.isClareOne) {
            this.cliqAddressInput.controls.serialNumber.setValidators([Validators.required]);
        } else {
            this.cliqAddressInput.controls.serialNumber.setValidators([]);
        }
    }

    getMACFromHost(uuid: string) {
        const matches = uuid.slice(uuid.length - 12).match(/([0-9a-fA-F]){1,2}/g);
        return matches.join(":").toUpperCase();
    }

    validateSerialNumber(initSerialNumber) {
        let isValid = false;
        const originalSerialNumber = this.getBaseValue(initSerialNumber).trim();
        if (originalSerialNumber) {
            const serialNumberLength = originalSerialNumber.length;
            if (serialNumberLength >= 8 && serialNumberLength <= 18) {
                isValid = true;
                this.isValidSerialNumber = true;
            } else {
                this.isValidSerialNumber = false;
            }
            if (!isValid) {
                this.cliqAddressInput.markAsTouched();
                this.cliqAddressInput.markAsDirty();
                this.fields.serialNumber.setErrors({
                    invalid: "Invalid Serial Number",
                });
            } else {
                this.cliqAddressInput.setErrors(null);
            }

            if (this.fields.serialNumber.value !== initSerialNumber) {
                this.cliqAddressInput.patchValue({
                    serialNumber: initSerialNumber,
                });
            }

            return originalSerialNumber;
        }
        if (!this.isClareOne) {
            this.isValidSerialNumber = true;
            this.cliqAddressInput.setErrors(null);
        }
    }

    verifyController() {
        this.isControllerVerified = false;
        this.isPendingVerification = true;
        this.isIncorrectSerialNumber = false;
        this.verificationFieldName = "Serial Number";
        this.chService
            .verifySerialNumber(this.cliqAddressInput.controls.controllerId.value, this.cliqAddressInput.controls.serialNumber.value)
            .pipe(
                map(isVerified => {
                    this.isSerialNumberVerified = isVerified;
                    this.isControllerVerified = isVerified;
                    this.isIncorrectSerialNumber = !isVerified;
                    this.isPendingVerification = false;
                    this.isControllerOnline = true;
                    if (this.raLinkConnectionValid && isVerified) {
                        const uuid = this.fields.controllerId.value;
                        this.getClareSecurityICCID(uuid);
                    }
                }),
                catchError(error => {
                    console.log(error);
                    this.isPendingVerification = false;
                    this.isControllerOnline = false;
                    return error;
                })
            )
            .subscribe(d => d);
    }

    drawLine(begin, end, color): void {
        this.canvasContext.beginPath();
        this.canvasContext.moveTo(begin.x, begin.y);
        this.canvasContext.lineTo(end.x, end.y);
        this.canvasContext.lineWidth = 4;
        this.canvasContext.strokeStyle = color;
        this.canvasContext.stroke();
    }

    tick(): void {
        if (this.video.readyState === this.video.HAVE_ENOUGH_DATA) {
            this.canvasContext.drawImage(this.video, 0, 0, this.canvasElement.nativeElement.width, this.canvasElement.nativeElement.height);

            this.imageData.next(
                this.canvasContext.getImageData(0, 0, this.canvasElement.nativeElement.width, this.canvasElement.nativeElement.height)
            );
        }
        requestAnimationFrame(this.tick.bind(this));
    }

    rescanCode() {
        if (this.isReadOnly) {
            return;
        }
        this.qrcodeDetected = false;
        this.videoDisabled = false;
        this.startVideo();
    }

    startVideo() {
        navigator.mediaDevices
            .enumerateDevices()
            .then(() =>
                navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: "environment",
                    },
                    audio: false,
                })
            )
            .then(async (stream: MediaStream) => {
                this.video.srcObject = stream;
                this.video.setAttribute("playsinline", "true"); // required to tell iOS safari we don't want fullscreen
                await this.video.play();
                this.canvasElement.nativeElement.height =
                    this.canvasElement.nativeElement.width * (this.video.videoHeight / this.video.videoWidth);
                requestAnimationFrame(this.tick.bind(this));
            })
            .catch(err => {
                console.error(err);
            });
    }

    testRaLinkConnection() {
        this.validateButtonMashed = true;
        const uuid = this.fields.controllerId.value;
        this.ralinkDatabaseService
            .getClareSystem$([uuid])
            .pipe(catchError(() => of([])))
            .subscribe(clareSystems => {
                const networkPropLength = Object.keys(clareSystems || {}).length;
                if (networkPropLength) {
                    this.raLinkConnectionValid = true;
                    this.getClareSecurityICCID(uuid);
                } else {
                    this.raLinkConnectionValid = false;
                }
                this.cdr.markForCheck();
            });
    }
    getClareSecurityICCID(uuid) {
        this.chService
            .getSecurityPanel(uuid)
            .pipe(
                take(1),
                map(panel => panel[0]),
                tap(clareSecurityDevice => {
                    const onlineIccid = clareSecurityDevice?.Properties?.ICCID;
                    if (clareSecurityDevice?.Properties?.ICCID) {
                        this.fields.iccid.setValue(onlineIccid);
                        this.fields.iccid.disable();
                    }
                })
            )
            .subscribe();
    }
    correctUuid() {
        const uuid = this.fields.controllerId.value;
        if (ProjectHelperService.isClareOne(uuid) && this.isValidUuid && uuid.indexOf(".000") === -1) {
            this.fields.controllerId.setValue(uuid + ".000");
        }
    }
}
